import {isValid} from 'date-fns';
import {DataStatus} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetBusinessCaseQuery, useGetVehicleV2Query} from '@dms/api/sales';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {getNewVehicleDeliveryFlag} from '@dms/features/new-vehicle';
import {VehicleWidget, getBusinessCaseTradeTypeFlag, getVehicleTradeType} from '@dms/shared';

import {DOT_CHARACTER, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

const isValidDate = (date: Date | null): date is Date => isNotNil(date) && isValid(date);

type VehicleOverviewProps = {
  vehicleId: string;
  businessCaseId: string;
  /**
   * Only purchase vehicles carry their trade type in the offer payload, sale vehicles do not.
   */
  isBrokerageOfferVehicle?: boolean;
} & RequiredTestIdProps;

export const VehicleOverview = (props: VehicleOverviewProps) => {
  const formatDateTime = useDateTimeFormatter();
  const {
    data: vehicle,
    isLoading: isLoadingVehicle,
    isError: isVehicleError,
  } = useGetVehicleV2Query({vehicleId: props.vehicleId ?? ''}, {skip: isNil(props.vehicleId)});

  // The offer sale vehicle carries no trade type, so it has to come from the sale vehicle
  const {data: saleVehicle} = useGetSaleVehicleQuery(
    {vehicleId: props.vehicleId},
    {skip: isNilOrEmpty(props.vehicleId)}
  );

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: props.businessCaseId},
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const tradeType = props.isBrokerageOfferVehicle
    ? 'BROKERAGE'
    : getVehicleTradeType(saleVehicle, businessCase);

  // An in stock vehicle is indistinguishable from a brokerage one whose sale vehicle has not
  // resolved yet, so withhold the flag rather than showing a wrong one - the same way the business
  // case header does
  const tradeTypeFlag =
    tradeType === 'BROKERAGE' || isNotNil(saleVehicle)
      ? getBusinessCaseTradeTypeFlag(tradeType)
      : undefined;

  // An ordered new vehicle keeps its raw trade type until arrival, so the arrival facts (D53) win
  // over it - the same precedence as the header and the Selling section
  const deliveryFlag = getNewVehicleDeliveryFlag(saleVehicle);
  // The ETA only exists while the car is incoming; anything unparseable falls back to the bare flag
  const incomingEta =
    saleVehicle?.incoming === true && isNotNilOrEmpty(saleVehicle.incomingEta)
      ? parseDate(saleVehicle.incomingEta)
      : null;
  const flag = match({deliveryFlag, incomingEta})
    .with({deliveryFlag: Pattern.nonNullable, incomingEta: Pattern.when(isValidDate)}, (state) => ({
      colorScheme: state.deliveryFlag.colorScheme,
      label: `${state.deliveryFlag.label} ${DOT_CHARACTER} ${formatDateTime('dateShort', state.incomingEta)}`,
    }))
    .with({deliveryFlag: Pattern.nonNullable}, (state) => ({
      colorScheme: state.deliveryFlag.colorScheme,
      label: state.deliveryFlag.label,
    }))
    .otherwise(() => tradeTypeFlag);

  return (
    <DataStatus isLoading={isLoadingVehicle} isError={isVehicleError}>
      <VehicleWidget
        data-testid={suffixTestId('vehicleOverview', props)}
        key={vehicle?.id}
        vehicle={vehicle!}
        flags={isNotNil(flag) ? [flag] : undefined}
      />
    </DataStatus>
  );
};
